import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiBarChart2, FiCopy, FiCheck, FiX, FiTag, FiCalendar, FiPercent, FiDollarSign } from 'react-icons/fi';
import axios from 'axios';

const DAYS_OF_WEEK = [
    { label: 'Sun', value: 0 },
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
];

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [vendors, setVendors] = useState([]);
    const [vendorSearch, setVendorSearch] = useState('');
    const [showVendorDropdown, setShowVendorDropdown] = useState(false);

    // New coupon form state
    const [newCoupon, setNewCoupon] = useState({
        code: '',
        name: '',
        description: '',
        type: 'general',
        vendorId: '',
        fundedBy: 'platform',
        discountType: 'percentage',
        discountValue: '',
        maxDiscountAmount: '',
        minOrderValue: '',
        usageLimitType: 'unlimited',
        totalUsageLimit: '',
        perUserUsageLimit: 1,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        terms: '',
        companyName: '',
        excludedDays: []
    });

    // Edit coupon form state
    const [editCoupon, setEditCoupon] = useState(null);

    // Excel upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedPhoneNumbers, setUploadedPhoneNumbers] = useState([]);
    const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);

    const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchCoupons();
        fetchVendors();
    }, [filterType]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showVendorDropdown && !event.target.closest('.vendor-dropdown-container')) {
                setShowVendorDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showVendorDropdown]);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const params = filterType !== 'all' ? `?type=${filterType}` : '';
            const response = await axios.get(`${API_BASE_URL}/coupon/admin/coupons${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCoupons(response.data.data.coupons);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            alert('Failed to load coupons');
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            console.log('Fetching vendors from:', `${API_BASE_URL}/admin/vendor/list`);
            const response = await axios.get(`${API_BASE_URL}/admin/vendor/list`, {
                params: {
                    page: 1,
                    limit: 1000, // Get all vendors
                    approval: true // Only approved vendors
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Vendors API Response:', response.data);

            // Handle different possible response structures
            const vendorsList = response.data.data?.vendors || response.data.vendors || response.data || [];
            console.log('Extracted vendors list:', vendorsList);
            setVendors(vendorsList);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            console.error('Error details:', error.response?.data);
        }
    };

    const handleCreateCoupon = async (e) => {
        e.preventDefault();
        
        try {
            const couponData = {
                ...newCoupon,
                eligiblePhoneNumbers: uploadedPhoneNumbers
            };

            await axios.post(`${API_BASE_URL}/coupon/admin/coupons`, couponData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Coupon created successfully!');
            setShowCreateModal(false);
            resetForm();
            fetchCoupons();
        } catch (error) {
            console.error('Error creating coupon:', error);
            alert(error.response?.data?.message || 'Failed to create coupon');
        }
    };

    const handleUploadExcel = async (e) => {
        e.preventDefault();
        
        if (!selectedFile) {
            alert('Please select a file');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/coupon/admin/coupons/upload-employees`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setUploadedPhoneNumbers(response.data.data.phoneNumbers);
            setShowPhoneNumbers(true);
            alert(`Successfully uploaded ${response.data.data.validPhoneNumbers} phone numbers`);
        } catch (error) {
            console.error('Error uploading file:', error);
            alert(error.response?.data?.message || 'Failed to upload file');
        }
    };

    const handleDeleteCoupon = async (couponId) => {
        if (!window.confirm('Are you sure you want to delete this coupon?')) {
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL}/coupon/admin/coupons/${couponId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Coupon deleted successfully!');
            fetchCoupons();
        } catch (error) {
            console.error('Error deleting coupon:', error);
            alert(error.response?.data?.message || 'Failed to delete coupon');
        }
    };

    const handleUpdateCoupon = async (e) => {
        e.preventDefault();

        try {
            const { _id, name, description, discountValue, maxDiscountAmount, minOrderValue,
                totalUsageLimit, perUserUsageLimit, endDate, isActive, terms, companyName, excludedDays, fundedBy } = editCoupon;

            const updateData = {
                name,
                description,
                discountValue: Number(discountValue),
                minOrderValue: minOrderValue === '' ? 0 : Number(minOrderValue),
                endDate,
                isActive,
                terms,
                companyName,
                excludedDays,
                fundedBy
            };

            if (editCoupon.discountType === 'percentage') {
                updateData.maxDiscountAmount = maxDiscountAmount === '' ? null : Number(maxDiscountAmount);
            }

            if (editCoupon.usageLimitType === 'total') {
                updateData.totalUsageLimit = totalUsageLimit === '' ? null : Number(totalUsageLimit);
            }

            if (editCoupon.usageLimitType === 'per_user') {
                updateData.perUserUsageLimit = perUserUsageLimit === '' ? 1 : Number(perUserUsageLimit);
            }

            await axios.put(`${API_BASE_URL}/coupon/admin/coupons/${_id}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Coupon updated successfully!');
            setShowEditModal(false);
            setEditCoupon(null);
            fetchCoupons();
        } catch (error) {
            console.error('Error updating coupon:', error);
            alert(error.response?.data?.message || 'Failed to update coupon');
        }
    };

    const toggleExcludedDay = (day, isEdit = false) => {
        if (isEdit) {
            setEditCoupon(prev => {
                const current = prev.excludedDays || [];
                const next = current.includes(day)
                    ? current.filter(d => d !== day)
                    : [...current, day];
                return { ...prev, excludedDays: next };
            });
        } else {
            setNewCoupon(prev => {
                const current = prev.excludedDays || [];
                const next = current.includes(day)
                    ? current.filter(d => d !== day)
                    : [...current, day];
                return { ...prev, excludedDays: next };
            });
        }
    };

    const openEditModal = (coupon) => {
        setEditCoupon({
            _id: coupon._id,
            name: coupon.name,
            description: coupon.description || '',
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            maxDiscountAmount: coupon.maxDiscountAmount ?? '',
            minOrderValue: coupon.minOrderValue ?? '',
            usageLimitType: coupon.usageLimitType,
            totalUsageLimit: coupon.totalUsageLimit ?? '',
            perUserUsageLimit: coupon.perUserUsageLimit ?? 1,
            endDate: coupon.endDate ? coupon.endDate.split('T')[0] : '',
            isActive: coupon.isActive,
            terms: coupon.terms || '',
            companyName: coupon.companyName || '',
            excludedDays: coupon.excludedDays || [],
            type: coupon.type,
            fundedBy: coupon.fundedBy || 'platform'
        });
        setShowEditModal(true);
    };

    const handleToggleStatus = async (couponId) => {
        try {
            await axios.patch(
                `${API_BASE_URL}/coupon/admin/coupons/${couponId}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            alert('Coupon status updated!');
            fetchCoupons();
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to update coupon status');
        }
    };

    const copyCouponCode = (code) => {
        navigator.clipboard.writeText(code);
        alert('Coupon code copied to clipboard!');
    };

    const resetForm = () => {
        setNewCoupon({
            code: '',
            name: '',
            description: '',
            type: 'general',
            vendorId: '',
            fundedBy: 'platform',
            discountType: 'percentage',
            discountValue: '',
            maxDiscountAmount: '',
            minOrderValue: '',
            usageLimitType: 'unlimited',
            totalUsageLimit: '',
            perUserUsageLimit: 1,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            terms: '',
            companyName: '',
            excludedDays: []
        });
        setUploadedPhoneNumbers([]);
        setShowPhoneNumbers(false);
        setSelectedFile(null);
        setVendorSearch('');
        setShowVendorDropdown(false);
    };

    const filteredCoupons = coupons.filter(coupon =>
        coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coupon.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredVendors = vendors.filter(vendor =>
        vendor.store_name?.toLowerCase().includes(vendorSearch.toLowerCase())
    );

    const getSelectedVendorName = () => {
        const vendor = vendors.find(v => v._id === newCoupon.vendorId);
        return vendor ? vendor.store_name : '';
    };

    const getCouponTypeColor = (type) => {
        switch(type) {
            case 'general': return 'bg-blue-100 text-blue-700';
            case 'vendor': return 'bg-purple-100 text-purple-700';
            case 'employee': return 'bg-green-100 text-green-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getCouponTypeBadge = (type) => {
        switch(type) {
            case 'general': return 'General';
            case 'vendor': return 'Vendor';
            case 'employee': return 'Employee/Company';
            default: return type;
        }
    };

    return (
        <div className="card p-5 md:p-8">
            <div className="mb-10">
                <p className="text-3xl font-extrabold tracking-tight text-slate-900">
                    Coupon Management
                </p>
                <p className="text-gray-500 mt-2">Create and manage discount coupons</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                    <FiPlus /> Create Coupon
                </button>               
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">All Types</option>
                    <option value="general">General</option>
                    <option value="vendor">Vendor</option>
                    <option value="employee">Employee/Company</option>
                </select>

                <input
                    type="text"
                    placeholder="Search by code or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Coupons List */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            ) : filteredCoupons.length === 0 ? (
                <div className="text-center py-20">
                    <FiTag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No coupons found</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCoupons.map((coupon) => (
                        <div
                            key={coupon._id}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCouponTypeColor(coupon.type)}`}>
                                            {getCouponTypeBadge(coupon.type)}
                                        </span>
                                        {coupon.isActive ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                                        ) : (
                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Inactive</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="bg-gray-100 px-3 py-1 rounded font-mono text-sm font-bold border-2 border-dashed border-gray-400">
                                            {coupon.code}
                                        </span>
                                        <button
                                            onClick={() => copyCouponCode(coupon.code)}
                                            className="p-1 hover:bg-gray-100 rounded"
                                        >
                                            <FiCopy className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <h3 className="font-semibold text-lg mb-2">{coupon.name}</h3>
                            {coupon.description && (
                                <p className="text-sm text-gray-600 mb-3">{coupon.description}</p>
                            )}

                            {/* Discount Info */}
                            <div className="flex items-center gap-4 mb-3 text-sm">
                                <div className="flex items-center gap-1">
                                    {coupon.discountType === 'percentage' ? (
                                        <>
                                            <FiPercent className="w-4 h-4 text-blue-500" />
                                            <span className="font-semibold text-blue-600">{coupon.discountValue}% OFF</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiDollarSign className="w-4 h-4 text-green-500" />
                                            <span className="font-semibold text-green-600">₹{coupon.discountValue} OFF</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div className="text-xs text-gray-500 space-y-1 mb-3">
                                {coupon.minOrderValue > 0 && (
                                    <p>• Min Order: ₹{coupon.minOrderValue}</p>
                                )}
                                {coupon.maxDiscountAmount && (
                                    <p>• Max Discount: ₹{coupon.maxDiscountAmount}</p>
                                )}
                                {coupon.type === 'vendor' && coupon.vendorId && (
                                    <p>• Vendor: {coupon.vendorId.store_name}</p>
                                )}
                                <p>• Funded by: {coupon.fundedBy === 'vendor' ? 'Vendor' : 'Bookwie'}</p>
                                {coupon.type === 'employee' && coupon.companyName && (
                                    <p>• Company: {coupon.companyName}</p>
                                )}
                                {coupon.type === 'employee' && coupon.metadata?.totalEligibleUsers && (
                                    <p>• Eligible Users: {coupon.metadata.totalEligibleUsers}</p>
                                )}
                                {coupon.excludedDays?.length > 0 && (
                                    <p>• Not valid on: {coupon.excludedDays
                                        .map(d => DAYS_OF_WEEK.find(dow => dow.value === d)?.label)
                                        .join(', ')}</p>
                                )}
                            </div>

                            {/* Usage Stats */}
                            <div className="bg-gray-50 rounded p-2 mb-3 text-xs">
                                <p className="text-gray-700">
                                    Used: <span className="font-semibold">{coupon.totalUsedCount}</span>
                                    {coupon.usageLimitType === 'total' && coupon.totalUsageLimit && (
                                        <span> / {coupon.totalUsageLimit}</span>
                                    )}
                                </p>
                                <p className="text-gray-500 mt-1">
                                    Expires: {new Date(coupon.endDate).toLocaleDateString()}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openEditModal(coupon)}
                                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                    <FiEdit2 />
                                </button>
                                <button
                                    onClick={() => handleToggleStatus(coupon._id)}
                                    className={`flex-1 px-3 py-2 rounded text-sm font-medium ${
                                        coupon.isActive
                                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                    }`}
                                >
                                    {coupon.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                    onClick={() => handleDeleteCoupon(coupon._id)}
                                    disabled={coupon.totalUsedCount > 0}
                                    className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Coupon Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
                            <h3 className="text-xl font-bold">Create New Coupon</h3>
                            <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateCoupon} className="p-6 space-y-4">
                            {/* Coupon Code */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Coupon Code *</label>
                                <input
                                    type="text"
                                    required
                                    value={newCoupon.code}
                                    onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                                    placeholder="e.g., SAVE20"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
                                />
                            </div>

                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={newCoupon.name}
                                    onChange={(e) => setNewCoupon({...newCoupon, name: e.target.value})}
                                    placeholder="e.g., 20% Off on All Services"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Description</label>
                                <textarea
                                    value={newCoupon.description}
                                    onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                                    placeholder="Brief description of the offer"
                                    rows="2"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Coupon Type *</label>
                                <select
                                    value={newCoupon.type}
                                    onChange={(e) => setNewCoupon({...newCoupon, type: e.target.value, vendorId: '', companyName: '', fundedBy: e.target.value === 'vendor' ? '' : 'platform'})}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="general">General (All Users)</option>
                                    <option value="vendor">Vendor Specific</option>
                                    <option value="employee">Employee/Company</option>
                                </select>
                            </div>

                            {/* Vendor Selection */}
                            {newCoupon.type === 'vendor' && (
                                <div className="relative vendor-dropdown-container">
                                    <label className="block text-sm font-medium mb-2">Select Vendor *</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            required
                                            value={vendorSearch || getSelectedVendorName()}
                                            onChange={(e) => {
                                                setVendorSearch(e.target.value);
                                                setShowVendorDropdown(true);
                                                if (!e.target.value) {
                                                    setNewCoupon({...newCoupon, vendorId: ''});
                                                }
                                            }}
                                            onFocus={() => setShowVendorDropdown(true)}
                                            placeholder="Search and select vendor..."
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                        {showVendorDropdown && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {filteredVendors.length > 0 ? (
                                                    filteredVendors.map(vendor => (
                                                        <div
                                                            key={vendor._id}
                                                            onClick={() => {
                                                                setNewCoupon({...newCoupon, vendorId: vendor._id});
                                                                setVendorSearch('');
                                                                setShowVendorDropdown(false);
                                                            }}
                                                            className={`px-4 py-2 hover:bg-blue-50 cursor-pointer ${
                                                                newCoupon.vendorId === vendor._id ? 'bg-blue-100' : ''
                                                            }`}
                                                        >
                                                            <div className="font-medium">{vendor.store_name}</div>
                                                            {vendor.email && (
                                                                <div className="text-xs text-gray-500">{vendor.email}</div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-3 text-gray-500 text-sm">
                                                        {vendors.length === 0 ? 'No vendors available' : 'No vendors found'}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {newCoupon.vendorId && !showVendorDropdown && (
                                        <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                            <FiCheck /> Selected: {getSelectedVendorName()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Who pays for the discount */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Who pays for the discount? *</label>
                                <select
                                    required
                                    value={newCoupon.fundedBy}
                                    disabled={newCoupon.type !== 'vendor'}
                                    onChange={(e) => setNewCoupon({...newCoupon, fundedBy: e.target.value})}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                >
                                    <option value="" disabled>Select who funds this coupon</option>
                                    <option value="platform">Bookwie (platform-initiated) — vendor is paid in full</option>
                                    <option value="vendor">Vendor (vendor-initiated) — deducted from vendor earnings</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    {newCoupon.type === 'vendor'
                                        ? 'Choose Vendor if the salon asked for this offer.'
                                        : 'General and employee coupons are always funded by Bookwie.'}
                                </p>
                            </div>

                            {/* Company Name */}
                            {newCoupon.type === 'employee' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Company Name</label>
                                        <input
                                            type="text"
                                            value={newCoupon.companyName}
                                            onChange={(e) => setNewCoupon({...newCoupon, companyName: e.target.value})}
                                            placeholder="e.g., Acme Corporation"
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    {/* Upload Employee List Button */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <p className="text-sm text-blue-800 mb-2">Employee List Required</p>
                                        <button
                                            type="button"
                                            onClick={() => setShowUploadModal(true)}
                                            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
                                        >
                                            <FiUpload /> Upload Excel/CSV
                                        </button>
                                        {uploadedPhoneNumbers.length > 0 && (
                                            <p className="text-sm text-green-600 mt-2">
                                                ✓ {uploadedPhoneNumbers.length} phone numbers uploaded
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Discount Type and Value */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Discount Type *</label>
                                    <select
                                        value={newCoupon.discountType}
                                        onChange={(e) => setNewCoupon({...newCoupon, discountType: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="fixed">Fixed Amount (₹)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {newCoupon.discountType === 'percentage' ? 'Discount %' : 'Discount Amount'} *
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        max={newCoupon.discountType === 'percentage' ? '100' : undefined}
                                        value={newCoupon.discountValue}
                                        onChange={(e) => setNewCoupon({...newCoupon, discountValue: e.target.value})}
                                        placeholder={newCoupon.discountType === 'percentage' ? '20' : '100'}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Min Order and Max Discount */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Min Order Value (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={newCoupon.minOrderValue}
                                        onChange={(e) => setNewCoupon({...newCoupon, minOrderValue: e.target.value})}
                                        placeholder="0"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                {newCoupon.discountType === 'percentage' && (
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Max Discount (₹)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={newCoupon.maxDiscountAmount}
                                            onChange={(e) => setNewCoupon({...newCoupon, maxDiscountAmount: e.target.value})}
                                            placeholder="No limit"
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Usage Limit */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Usage Limit *</label>
                                <select
                                    value={newCoupon.usageLimitType}
                                    onChange={(e) => setNewCoupon({...newCoupon, usageLimitType: e.target.value})}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="unlimited">Unlimited</option>
                                    <option value="total">Total Usage Limit</option>
                                    <option value="per_user">Per User Limit</option>
                                </select>
                            </div>

                            {newCoupon.usageLimitType === 'total' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Total Uses *</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={newCoupon.totalUsageLimit}
                                        onChange={(e) => setNewCoupon({...newCoupon, totalUsageLimit: e.target.value})}
                                        placeholder="100"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {newCoupon.usageLimitType === 'per_user' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Uses Per User *</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={newCoupon.perUserUsageLimit}
                                        onChange={(e) => setNewCoupon({...newCoupon, perUserUsageLimit: e.target.value})}
                                        placeholder="1"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {/* Validity Period */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Start Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={newCoupon.startDate}
                                        onChange={(e) => setNewCoupon({...newCoupon, startDate: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">End Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={newCoupon.endDate}
                                        min={newCoupon.startDate}
                                        onChange={(e) => setNewCoupon({...newCoupon, endDate: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Exclude Peak Days */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Exclude on Days (peak days)</label>
                                <p className="text-xs text-gray-500 mb-2">Coupon cannot be applied on the selected days of the week.</p>
                                <div className="flex flex-wrap gap-2">
                                    {DAYS_OF_WEEK.map(day => (
                                        <button
                                            key={day.value}
                                            type="button"
                                            onClick={() => toggleExcludedDay(day.value)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                                newCoupon.excludedDays.includes(day.value)
                                                    ? 'bg-red-500 text-white border-red-500'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Terms */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Terms & Conditions</label>
                                <textarea
                                    value={newCoupon.terms}
                                    onChange={(e) => setNewCoupon({...newCoupon, terms: e.target.value})}
                                    placeholder="Enter terms and conditions"
                                    rows="3"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Submit */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                >
                                    Create Coupon
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Coupon Modal */}
            {showEditModal && editCoupon && (
                <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
                            <h3 className="text-xl font-bold">Edit Coupon</h3>
                            <button onClick={() => { setShowEditModal(false); setEditCoupon(null); }} className="p-2 hover:bg-gray-100 rounded">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateCoupon} className="p-6 space-y-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={editCoupon.name}
                                    onChange={(e) => setEditCoupon({...editCoupon, name: e.target.value})}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Description</label>
                                <textarea
                                    value={editCoupon.description}
                                    onChange={(e) => setEditCoupon({...editCoupon, description: e.target.value})}
                                    rows="2"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Who pays for the discount (vendor coupons only) */}
                            {editCoupon.type === 'vendor' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Who pays for the discount? *</label>
                                    <select
                                        value={editCoupon.fundedBy}
                                        onChange={(e) => setEditCoupon({...editCoupon, fundedBy: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="platform">Bookwie (platform-initiated) — vendor is paid in full</option>
                                        <option value="vendor">Vendor (vendor-initiated) — deducted from vendor earnings</option>
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">Applies to new bookings only; past bookings keep their original funding.</p>
                                </div>
                            )}

                            {/* Discount Value and Min Order */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        {editCoupon.discountType === 'percentage' ? 'Discount %' : 'Discount Amount'} *
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        max={editCoupon.discountType === 'percentage' ? '100' : undefined}
                                        value={editCoupon.discountValue}
                                        onChange={(e) => setEditCoupon({...editCoupon, discountValue: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Min Order Value (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={editCoupon.minOrderValue}
                                        onChange={(e) => setEditCoupon({...editCoupon, minOrderValue: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {editCoupon.discountType === 'percentage' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Max Discount (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={editCoupon.maxDiscountAmount}
                                        onChange={(e) => setEditCoupon({...editCoupon, maxDiscountAmount: e.target.value})}
                                        placeholder="No limit"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {editCoupon.usageLimitType === 'total' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Total Uses *</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={editCoupon.totalUsageLimit}
                                        onChange={(e) => setEditCoupon({...editCoupon, totalUsageLimit: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {editCoupon.usageLimitType === 'per_user' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Uses Per User *</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={editCoupon.perUserUsageLimit}
                                        onChange={(e) => setEditCoupon({...editCoupon, perUserUsageLimit: e.target.value})}
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}

                            {/* End Date */}
                            <div>
                                <label className="block text-sm font-medium mb-2">End Date *</label>
                                <input
                                    type="date"
                                    required
                                    value={editCoupon.endDate}
                                    onChange={(e) => setEditCoupon({...editCoupon, endDate: e.target.value})}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Exclude Peak Days */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Exclude on Days (peak days)</label>
                                <p className="text-xs text-gray-500 mb-2">Coupon cannot be applied on the selected days of the week.</p>
                                <div className="flex flex-wrap gap-2">
                                    {DAYS_OF_WEEK.map(day => (
                                        <button
                                            key={day.value}
                                            type="button"
                                            onClick={() => toggleExcludedDay(day.value, true)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                                editCoupon.excludedDays.includes(day.value)
                                                    ? 'bg-red-500 text-white border-red-500'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Terms */}
                            <div>
                                <label className="block text-sm font-medium mb-2">Terms & Conditions</label>
                                <textarea
                                    value={editCoupon.terms}
                                    onChange={(e) => setEditCoupon({...editCoupon, terms: e.target.value})}
                                    rows="3"
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Submit */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => { setShowEditModal(false); setEditCoupon(null); }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Upload Employee List Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-lg w-full">
                        <div className="border-b p-6 flex items-center justify-between">
                            <h3 className="text-xl font-bold">Upload Employee List</h3>
                            <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Instructions */}
                            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-blue-900 mb-2">Instructions</h4>
                                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                                    <li>Upload a CSV file with employee phone numbers</li>
                                    <li>File must have a column header named "phone" or "phoneNumber"</li>
                                    <li>Phone numbers should be 10 digits (Indian format)</li>
                                    <li>Example format: 9876543210</li>
                                </ul>
                            </div>

                            {/* Download Template */}
                            <div className="mb-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const csvContent = 'phoneNumber\n9876543210\n9876543211\n9876543212';
                                        const blob = new Blob([csvContent], { type: 'text/csv' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'employee_phone_template.csv';
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                    }}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                >
                                    <FiUpload className="rotate-180" /> Download CSV Template
                                </button>
                            </div>

                            <form onSubmit={handleUploadExcel}>
                                {/* File Upload */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2">Select CSV File</label>
                                    <div className="relative">
                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={(e) => {
                                                setSelectedFile(e.target.files[0]);
                                                setShowPhoneNumbers(false);
                                            }}
                                            className="w-full px-4 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </div>
                                    {selectedFile && (
                                        <p className="text-xs text-gray-600 mt-2">
                                            Selected: {selectedFile.name}
                                        </p>
                                    )}
                                </div>

                                {/* Success Message */}
                                {showPhoneNumbers && uploadedPhoneNumbers.length > 0 && (
                                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center gap-2 mb-3">
                                            <FiCheck className="w-5 h-5 text-green-600" />
                                            <p className="text-sm font-semibold text-green-800">
                                                Successfully uploaded {uploadedPhoneNumbers.length} phone numbers
                                            </p>
                                        </div>
                                        <div className="max-h-40 overflow-y-auto bg-white rounded p-2 border border-green-200">
                                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700 font-mono">
                                                {uploadedPhoneNumbers.slice(0, 20).map((phone, i) => (
                                                    <div key={i} className="flex items-center gap-1">
                                                        <span className="text-green-600">✓</span>
                                                        <span>{phone}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {uploadedPhoneNumbers.length > 20 && (
                                                <div className="text-xs text-green-700 mt-2 text-center font-medium">
                                                    + {uploadedPhoneNumbers.length - 20} more numbers
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            if (uploadedPhoneNumbers.length > 0) {
                                                setShowPhoneNumbers(false);
                                            }
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        {uploadedPhoneNumbers.length > 0 ? 'Done' : 'Cancel'}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!selectedFile || showPhoneNumbers}
                                        className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                                    >
                                        <FiUpload /> {showPhoneNumbers ? 'Uploaded' : 'Upload'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Coupons;

